import classNames from "classnames";
import React from "react";
import { useCallback } from "react";
import { connect } from "react-redux";
import * as zenscroll from "zenscroll";
import * as DateUtil from "@travellocal/utils/dateHelpers";
import { ActionPromises } from "../../../web/state/actionPromises";
import { MatchState } from "../../helpers/reduxHelpers";
import { neverHere } from "@travellocal/utils/tsHelpers";
import { ToolbarOptions } from "../textEditor";
import { Message } from "./message";
import { MessageEditor } from "./messageEditor";
import { NewMessageField } from "./newMessageField";

import "./thread.scss";

interface OwnProps {
  enquiry: IEnquiryOutput;
  className?: string;
  itineraryRoute: string;
}

interface StateProps {
  user: IState<IUser>;
  messageThread: IMessageThread;
}

interface DispatchProps {
  /**
   * Send the message currently stored as a draft for this message thread.
   */
  sendMessage: (message: string, snippets?: number[]) => Promise<void>;

  /**
   * Save a message to the client-side draft store.
   */
  saveDraftMessage: (message: string) => Promise<void>;

  /**
   * Reload the enquiry
   */
  reloadEnquiry: (enquiryId: number) => Promise<void>;
}

type Props = OwnProps & DispatchProps & StateProps;

const stateToProps = (state: ISharedStoreState, ownProps: OwnProps): StateProps => {
  const enquiryId = ownProps.enquiry ? ownProps.enquiry.id : null;
  let messageThread: IMessageThread;
  if (enquiryId && state.messagesByEnquiryId.data && state.messagesByEnquiryId.data[enquiryId] != null) {
    messageThread = state.messagesByEnquiryId.data[enquiryId];
  }

  return {
    user: state.user,
    messageThread,
  };
};

const mergeProps = (stateProps: StateProps, { dispatch }, ownProps: OwnProps): Props => {
  return {
    ...ownProps,
    ...stateProps,
    sendMessage: (html: string, snippets?: number[]) =>
      dispatch(ActionPromises.sendEnquiryMessage(ownProps.enquiry.id, stateProps.user.data, html, snippets)),
    saveDraftMessage: (html: string) => dispatch(ActionPromises.saveDraftMessage(ownProps.enquiry.id, html)),
    reloadEnquiry: (enquiryId: number) => dispatch(ActionPromises.enquiryById(enquiryId)),
  };
};

const Thread = (props: Props) => {

  // Centre the editor when called.
  const focusNewMessage = useCallback(() => {
    const editor = document.querySelector(".messaging div[contenteditable]") as HTMLElement;
    if (editor) {
      zenscroll.center(editor, 1000);
      editor.focus();
    }
  }, []);

  if (!props.enquiry || !props.messageThread) {
    return null;
  }

  return (
    <div className={classNames("messaging", props.className)}>
      {MatchState(props.user, {
        data: u => {
          switch (u.userType) {
            case UserType.Customer:

              return (
                <NewMessageField
                  draft={props.messageThread.draft}
                  userType={u.userType}
                  saveDraftMessage={props.saveDraftMessage}
                  sendMessage={async () => {
                    const sendMessage = await props.sendMessage(props.messageThread.draft.data);

                    if (!props.enquiry.isConnected) {
                      // Reload the enquiry to see if it's been connnected after the message
                      await props.reloadEnquiry(props.enquiry.id);
                    }

                    return sendMessage;
                  }}
                  enquiry={props.enquiry}
                  messageThread={props.messageThread}
                />
              );

            case UserType.LtoEmployee:
            case UserType.Administrator:

              return (
                <MessageEditor
                  user={u}
                  message={props.messageThread.draft.data}
                  saveDraftMessage={props.saveDraftMessage}
                  sendMessage={(m) => props.sendMessage(m, props.messageThread.snippets?.data ?? [])}
                  toolbarOptions={[
                    ToolbarOptions.Minimal,
                    ToolbarOptions.PriceCalculator,
                    ToolbarOptions.MessageSnippets,
                  ]}
                />
              );

              return null;
            default:
              neverHere(u.userType);
          }
        },
      })}

      {MatchState(props.messageThread.thread, {
        data: messages => {

          const threadStartDate = messages?.length
            ? DateUtil.formatDateTime(messages[messages.length - 1].sentTime, "MMMM Do YYYY [at] HH:mm")
            : null;

          const latestReplyFromLto = messages.find(message => message.toCustomer);
          const latestReplyFromCustomer = messages.find(message => !message.toCustomer);

          return (
            <div className="thread">
              {messages.map(message => (
                <Message
                  key={message.id}
                  message={message}
                  enquiry={props.enquiry}
                  onReplyClick={focusNewMessage}
                  userContext={props.user.data.userType}
                  latestReplyFromLto={latestReplyFromLto?.id}
                  latestReplyFromCustomer={latestReplyFromCustomer?.id}
                />
              ))}

              <p className="end-marker">
                {threadStartDate
                  ? <small><em>Conversation started <time>{threadStartDate}</time></em></small>
                  : <small><em>There are no messages to show</em></small>
                }
              </p>
            </div>
          );
        },
      })}
    </div>
  );
};

export default connect(stateToProps, null, mergeProps)(Thread);
